import React, { useEffect, useMemo, useState } from "react";
import {
    Container,
    Typography,
    useMediaQuery,
    useTheme,
    Box,
    Skeleton,
    Alert,
    Grid,
    Chip,
    Stack,
    Paper,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Divider,
    Card,
    CardContent,
} from "@mui/material";
import axios from "axios";
import { API_URL } from "./config.js";
import {
    Description,
    QueryStatsRounded,
    ExpandMore,
    AccessTime,
    Devices,
    LocationOn,
    Language,
} from "@mui/icons-material";

const formatDateTime = (value) => {
    if (!value) return "—";
    const parsed = new Date(value);

    if (Number.isNaN(parsed.getTime())) {
        return value;
    }

    return parsed.toLocaleString();
};

const Analytics = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [analytics, setAnalytics] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchAnalytics = async () => {
            try {
                const response = await axios.get(API_URL + "get_analytics.php", {
                    withCredentials: true,
                });

                if (Array.isArray(response.data)) {
                    setAnalytics(response.data);
                } else {
                    setError("Unexpected data format received from server");
                }
            } catch (fetchError) {
                setError("Failed to load analytics data");
                console.error("Error fetching analytics:", fetchError);
            } finally {
                setLoading(false);
            }
        };

        fetchAnalytics();
    }, []);

    const totalScans = useMemo(
        () => analytics.reduce((sum, item) => sum + (Number(item.scans ?? item.views) || 0), 0),
        [analytics]
    );

    const totalEvents = useMemo(
        () => analytics.reduce((sum, item) => sum + (Array.isArray(item.entries) ? item.entries.length : 0), 0),
        [analytics]
    );

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 8, sm: 10 } }}>
            <Paper
                elevation={0}
                sx={{
                    p: { xs: 2.2, sm: 3.2 },
                    mb: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 3,
                    background: "linear-gradient(135deg, rgba(25,118,210,.08) 0%, rgba(156,39,176,.06) 100%)",
                }}
            >
                <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", md: "center" }}
                >
                    <Box>
                        <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                            <QueryStatsRounded color="primary" />
                            <Typography variant="overline" sx={{ letterSpacing: 1.1, fontWeight: 700 }}>
                                Analytics Overview
                            </Typography>
                        </Stack>
                        <Typography variant={isMobile ? "h4" : "h3"} sx={{ fontWeight: 800 }}>
                            Scan Analytics
                        </Typography>
                        <Typography color="text.secondary" mt={0.7}>
                            Tap any record to view every scan event and engagement detail.
                        </Typography>
                    </Box>
                    <Stack direction={{ xs: "row", md: "column" }} spacing={1} width={{ xs: "100%", md: "auto" }}>
                        <Chip color="primary" label={`Records: ${analytics.length}`} sx={{ fontWeight: 700 }} />
                        <Chip color="secondary" label={`Total scans: ${totalScans}`} sx={{ fontWeight: 700 }} />
                        <Chip variant="outlined" label={`Event entries: ${totalEvents}`} sx={{ fontWeight: 700 }} />
                    </Stack>
                </Stack>
            </Paper>

            {loading ? (
                <Grid container spacing={2}>
                    {[1, 2, 3, 4, 5].map((item) => (
                        <Grid item xs={12} key={item}>
                            <Skeleton variant="rectangular" width="100%" height={84} sx={{ borderRadius: 3 }} />
                        </Grid>
                    ))}
                </Grid>
            ) : error ? (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            ) : analytics.length === 0 ? (
                <Alert severity="info" sx={{ mb: 3 }}>
                    No analytics data available
                </Alert>
            ) : (
                <Stack spacing={1.5}>
                    {analytics.map((item) => {
                        const scans = Number(item.scans ?? item.views) || 0;
                        const entries = Array.isArray(item.entries) ? item.entries : [];

                        return (
                            <Accordion
                                key={item.id}
                                disableGutters
                                sx={{
                                    borderRadius: "18px !important",
                                    overflow: "hidden",
                                    border: "1px solid",
                                    borderColor: "divider",
                                    boxShadow: "0 8px 26px rgba(17, 24, 39, 0.05)",
                                }}
                            >
                                <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: { xs: 1.5, sm: 2.2 } }}>
                                    <Grid container spacing={1.5} alignItems="center">
                                        <Grid item xs={12} md={6.5}>
                                            <Typography variant="subtitle1" fontWeight={700} sx={{ wordBreak: "break-word" }}>
                                                <Description
                                                    sx={{
                                                        fontSize: "1.1rem",
                                                        color: "primary.main",
                                                        mr: 0.8,
                                                        verticalAlign: "middle",
                                                    }}
                                                />
                                                {item.name || "Unnamed record"}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6} sm={4} md={1.8}>
                                            <Typography variant="caption" color="text.secondary">
                                                Scans
                                            </Typography>
                                            <Typography variant="body1" fontWeight={700}>
                                                {scans}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6} sm={4} md={1.8}>
                                            <Typography variant="caption" color="text.secondary">
                                                Unique users
                                            </Typography>
                                            <Typography variant="body1" fontWeight={700}>
                                                {item.unique_users || 0}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={4} md={1.9}>
                                            <Typography variant="caption" color="text.secondary">
                                                Entries
                                            </Typography>
                                            <Typography variant="body1" fontWeight={700}>
                                                {entries.length}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </AccordionSummary>
                                <AccordionDetails sx={{ pt: 0, px: { xs: 1.5, sm: 2.2 }, pb: 2 }}>
                                    <Divider sx={{ mb: 1.6 }} />
                                    <Grid container spacing={1.4} mb={entries.length > 0 ? 1.3 : 0}>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Typography variant="body2">
                                                <strong>Last scan:</strong> {formatDateTime(item.scan_time)}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Typography variant="body2">
                                                <strong>Top device:</strong> {item.device || "Unknown"}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Typography variant="body2">
                                                <strong>Top location:</strong> {item.location || "Unknown"}
                                            </Typography>
                                        </Grid>
                                    </Grid>

                                    {entries.length === 0 ? (
                                        <Alert severity="info" variant="outlined">
                                            No scan entries found for this record.
                                        </Alert>
                                    ) : (
                                        <Grid container spacing={1.5}>
                                            {entries.map((entry, index) => (
                                                <Grid item xs={12} sm={6} lg={4} key={`${item.id}-${entry.scan_time}-${index}`}>
                                                    <Card
                                                        variant="outlined"
                                                        sx={{
                                                            height: "100%",
                                                            borderRadius: 2,
                                                            backgroundColor: "background.default",
                                                        }}
                                                    >
                                                        <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                                                            <Stack spacing={1}>
                                                                <Stack direction="row" spacing={0.8} alignItems="center">
                                                                    <AccessTime fontSize="small" color="primary" />
                                                                    <Typography variant="body2" fontWeight={700}>
                                                                        {formatDateTime(entry.scan_time)}
                                                                    </Typography>
                                                                </Stack>
                                                                <Stack direction="row" spacing={0.8} alignItems="center">
                                                                    <Devices fontSize="small" color="action" />
                                                                    <Typography variant="body2">
                                                                        {entry.device || "Unknown"} • {entry.os || "Unknown"}
                                                                    </Typography>
                                                                </Stack>
                                                                <Stack direction="row" spacing={0.8} alignItems="center">
                                                                    <LocationOn fontSize="small" color="action" />
                                                                    <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                                                                        {entry.location || "Unknown"}
                                                                    </Typography>
                                                                </Stack>
                                                                <Stack direction="row" spacing={0.8} alignItems="center">
                                                                    <Language fontSize="small" color="action" />
                                                                    <Typography variant="body2" sx={{ textTransform: "capitalize" }}>
                                                                        {entry.connection_type || "Unknown"} • {entry.day_of_week || "Unknown"} / {entry.time_of_day || "Unknown"}
                                                                    </Typography>
                                                                </Stack>
                                                            </Stack>
                                                        </CardContent>
                                                    </Card>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    )}
                                </AccordionDetails>
                            </Accordion>
                        );
                    })}
                </Stack>
            )}
        </Container>
    );
};

export default Analytics;
